// ---------------------------------------------------------------------------
// Who may read the chat, who may write to it, and how much of it is new.
//
// One module because all three questions are asked from two places — the panel
// itself and the room shell around it — and two copies of "is chat off" is how a
// room ends up with a chat button that opens an empty drawer.
//
// **`chatMode` is read through `chatModeOf`, never off `settings` directly.** A
// room restored from a snapshot written before the setting existed comes back
// without it, and an old client is handed one by a new server; both have to read
// as `everyone`, which is what every room had.
// ---------------------------------------------------------------------------

import type { ChatMessage, ChatMode, ClientGameState } from '@countdown/shared';

/**
 * Second gate on team privacy. The server already scopes team lines to the team
 * before it serialises state, and that is the gate that matters; this one costs
 * nothing and means a sanitisation slip does not become a rendered secret.
 *
 * A system line belongs to everyone: a kick or a word override is the reason the
 * room is talking, and hiding it would strand the replies.
 */
export function readableBy(message: ChatMessage, yourTeamId: string | null): boolean {
  if (message.system || message.channel !== 'team') return true;
  return yourTeamId !== null && message.teamId === yourTeamId;
}

/** See the note at the top: never `state.settings.chatMode` at a call site. */
export function chatModeOf(state: ClientGameState): ChatMode {
  return state.settings.chatMode ?? 'everyone';
}

/**
 * Whether there is a chat panel at all.
 *
 * `off` takes the whole panel off the screen rather than leaving a box nobody
 * can type in. The panel carries nothing else — the game log is `state.log` and
 * is not drawn here — so with the messages gone there is nothing left for it to
 * be, and the button and the unread badge go with it.
 *
 * The room shell reads this too, for the drawer and the header button. It has to
 * be the same answer in both places or the phone gets a button that opens an
 * empty drawer.
 */
export function chatPanelShown(state: ClientGameState): boolean {
  return chatModeOf(state) !== 'off';
}

/**
 * Whether this player may send.
 *
 * `players` leaves the spectator bench reading and not writing. The composer is
 * not drawn for them at all, with a line where it was saying why — a field that
 * accepts a sentence and then refuses it is worse than no field, because the
 * player has already spent the sentence.
 *
 * Being muted is a different refusal with a different sentence, and it is not
 * folded in here: one is about this player, the other is about the room.
 */
export function canSendChat(state: ClientGameState): boolean {
  if (chatModeOf(state) !== 'players') return true;
  const you = state.players.find((player) => player.id === state.youId) ?? null;
  return you?.role !== 'spectator';
}

/** Every line this player is allowed to see, in the order the room said them. */
export function readableChat(state: ClientGameState, yourTeamId: string | null): ChatMessage[] {
  return state.chat.filter((message) => readableBy(message, yourTeamId));
}

/**
 * The unread badge, and the mark it counts from.
 *
 * **It is a watermark over `at`, never a length.** The count used to be
 * `chat.length - seen`, which was correct only while a deletion left a tombstone
 * behind and the array never shrank. Deletion is a removal now — one line, or
 * every line one author ever said — so a length is a number that goes DOWN, and
 * a mark taken at the old length sits above the new total: `readable - seen` is
 * negative, clamps to zero, and the next few things anybody says light no badge
 * at all. That is the arithmetic the tombstone was carrying, and this is what
 * replaces it.
 *
 * A watermark cannot go backwards for the same reason: removing lines removes
 * candidates from the count and never adds one, so the worst a purge can do is
 * take a message out of a total that had already been counted, which is exactly
 * right — a line that no longer exists is not unread.
 *
 * `at` is the server's own `now` at the moment the line was appended, so it
 * increases down the array. Two lines inside one millisecond are indexed by the
 * same mark; the cost of the tie is that the second of them does not light the
 * badge on its own, which is a message and not a conversation. If a per-message
 * monotonic sequence number ever lands on `ChatMessage`, these two functions are
 * the only place that has to learn about it.
 */
export function chatMark(state: ClientGameState, yourTeamId: string | null): number {
  let mark = 0;
  for (const message of readableChat(state, yourTeamId)) {
    if (message.at > mark) mark = message.at;
  }
  return mark;
}

/** How many readable lines have arrived since `mark`. See `chatMark`. */
export function unreadChat(
  state: ClientGameState,
  yourTeamId: string | null,
  mark: number,
): number {
  return readableChat(state, yourTeamId).filter((message) => message.at > mark).length;
}
